import React, { useState } from 'react';
import Button from '../../../NecttosComp/Button/Button';
import Input from '../../../NecttosComp/Input/Input';
import { Module } from '../../types/lms.types';

interface AddModuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddModule: (module: Omit<Module, 'id'>) => void;
  existingModules: Module[];
}

const AddModuleModal: React.FC<AddModuleModalProps> = ({
  isOpen,
  onClose,
  onAddModule,
  existingModules
}) => {
  const [moduleName, setModuleName] = useState('');
  const [moduleDescription, setModuleDescription] = useState('');

  const getNextModuleNumber = () => {
    const moduleNumbers = existingModules
      .filter(m => m.id !== 'all')
      .map(m => {
        const match = m.name.match(/Module (\d+)/);
        return match ? parseInt(match[1]) : 0;
      });
    
    return moduleNumbers.length > 0 ? Math.max(...moduleNumbers) + 1 : 1;
  };

  const handleSubmit = () => {
    if (moduleName.trim()) {
      const moduleNumber = getNextModuleNumber();
      const fullModuleName = `Module ${moduleNumber}: ${moduleName.trim()}`;
      
      onAddModule({
        name: fullModuleName,
        description: moduleDescription.trim(),
        order: moduleNumber
      });

      // Reset form
      setModuleName('');
      setModuleDescription('');
      onClose();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Add New Module</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Module Name *
              </label>
              <Input
                fieldName="moduleName"
                type="text"
                idValue={moduleName}
                onChange={(e) => setModuleName(e.value)}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Will be prefixed with "Module {getNextModuleNumber()}:"
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description (Optional)
              </label>
              <textarea
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Brief description of the module..."
                rows={3}
                value={moduleDescription}
                onChange={(e) => setModuleDescription(e.target.value)}
                onKeyPress={handleKeyPress}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button type="close" onClick={onClose}>
              Cancel
            </Button>
            <Button type="save" onClick={handleSubmit}>
              Add Module
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddModuleModal;
